import { useState } from 'react';
import { Box, HStack, Select, useTheme, View, VStack } from 'native-base';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Button, Modal, SelectInput, Text } from '@pimm/base';
import { AddUpdateUserProfileRequest, RecoveryEmailStatusEnum, UserProfileDto } from '@pimm/services/lib/sms-tenants';
import { AddUpdateUserProfile, GetUserProfile } from '@pimm/services/lib/sms-tenants/services';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { CheckCircleBrokenIcon } from '../icons';
import { ListItem } from './list-item';
import { FormEmailRecovery } from './form-email-recovery';
import { FormPinSetup } from './form-pin-setup';
import { ModalConfirm } from './modal-confirm';
import { UserSecurityChangeEvent } from './user-security';
import { UserInformationChangeEvent } from './form-user-information';
import { FormContactEmail } from './form-contact-email';

type UserModalType = 'change-pin' | 'confirm-modal';

type UserSettingsReminderProps = {
  user?: UserProfileDto;
  onEndChange: (changeEvent: UserInformationChangeEvent | UserSecurityChangeEvent) => void;
  onClose?: () => void;
};

export const UserSettingsReminder = ({ user, ...props }: UserSettingsReminderProps) => {
  const { t } = useTranslation();
  const [selectedDefaultSiteId, setSelectedDefaultSiteId] = useState<string | undefined>(user?.defaultSiteId);
  const pinStatusText = user?.pinStatusText === 'Not Set' ? undefined : user?.pinStatusText;
  const [contactEmail, setContactEmail] = useState<string | undefined>(user?.contactEmail);

  const { colors } = useTheme();

  const modalFocus = useModalFocus<{ name: UserModalType }>();
  const modalName = modalFocus?.isOpen ? modalFocus.payload?.name : undefined;

  const addUpdateProfile = useMutation({
    mutationFn: async (payload: AddUpdateUserProfileRequest) => {
      await AddUpdateUserProfile(payload);
      // Get profile after successful update
      const profile = await GetUserProfile({ userId: payload.userId });
      return profile;
    },
    onSuccess: profile => {
      setSelectedDefaultSiteId(profile?.defaultSiteId);
      props?.onEndChange({ name: 'update-profile', data: profile });
    },
  });

  const handleConfirmChanges = () => {
    const payload: AddUpdateUserProfileRequest = {
      ...user,
      defaultSiteId: selectedDefaultSiteId,
      contactEmail: contactEmail,
    };

    addUpdateProfile.mutate(payload);
    modalFocus.setHide();
  };

  const handleFormChange = (name: UserSecurityChangeEvent['name']) => (payload?: UserSecurityChangeEvent['data']) => {
    props.onEndChange({ name: name, data: payload });
    modalFocus.setHide();
  };

  const handleOpenModal = (_name: UserModalType) => () => {
    modalFocus.setOpen({ name: _name });
  };

  const handleSelectChange = (siteId: string) => {
    setSelectedDefaultSiteId(siteId);
    if (siteId !== selectedDefaultSiteId) {
      handleOpenModal('confirm-modal')();
    }
  };

  const handleContactEmailChange = (email: string) => {
    setContactEmail(email);
    if (email !== user?.contactEmail) {
      handleOpenModal('confirm-modal')();
    }
  };

  return (
    <View height="full" w="full">
      <VStack space={4} p={4}>
        <VStack alignItems="flex-start" justifyContent="center">
          <Text size="xl" fontWeight={600} color="gray.900">
            {t('account_settings:update_user_info')}
          </Text>

          <Text size="md" fontWeight={400} color="gray.600">
            {t('account_settings:attention_required')}
          </Text>
        </VStack>

        <Box py={3} px={4} w="full" borderRadius="lg" justifyContent="center" bgColor={!user?.defaultSiteId ? 'error.100' : 'gray.50'}>
          <VStack space={1} zIndex={1000}>
            <VStack>
              <Text size="md" fontWeight={500} color="gray.700">
                {t('account_settings:default_site_access')}
              </Text>
              <SelectInput
                _actionSheetContent={{ bgColor: 'rose.100' }}
                onValueChange={handleSelectChange}
                selectedValue={selectedDefaultSiteId}
                placeholder={t('account_settings:choose_default_site_access')}
              >
                {user?.sites?.map(_site => (
                  <Select.Item key={_site?.id} value={_site?.id!} label={_site?.name ?? ''} />
                ))}
              </SelectInput>
            </VStack>
          </VStack>
        </Box>

        <ListItem
          _content={{ alignItems: 'flex-end' }}
          _title={{ justifyContent: 'center' }}
          py={3}
          px={4}
          w="full"
          borderRadius="lg"
          justifyContent="center"
          bgColor={!!pinStatusText ? 'gray.50' : 'error.100'}
          text={t('account_settings:pin')}
        >
          <Button minWidth={90} variant="unstyled" bg="white" onPress={handleOpenModal('change-pin')}>
            {!!pinStatusText ? t('account_settings:change') : t('account_settings:setup')}
          </Button>
        </ListItem>

        <FormContactEmail
          email={contactEmail}
          onEndChange={handleContactEmailChange}
          _container={{
            bg: !!user?.contactEmail ? 'gray.50' : 'error.100',
            borderWidth: 0,
          }}
        />

        <FormEmailRecovery
          email={user?.recoveryEmail}
          status={user?.recoveryEmailStatus}
          userId={user?.userId!}
          _container={{
            bg: user?.recoveryEmailStatus === RecoveryEmailStatusEnum.Verified ? 'gray.50' : 'error.100',
            borderWidth: 0,
          }}
          onEndChange={handleFormChange('update-recovery-email')}
        />

        {!!user?.defaultSiteId &&
        user?.recoveryEmailStatus === RecoveryEmailStatusEnum.Verified &&
        !!pinStatusText &&
        !!user?.contactEmail ? (
          <VStack space={4} alignItems="flex-end">
            <HStack space={2} py={3} px={4} borderRadius="lg" justifyContent="center" bgColor="success.100">
              <HStack alignItems="center" space={1}>
                <CheckCircleBrokenIcon size={4} color={colors.success[500]} />
                <Text size="md" fontWeight={500} color="success.500">
                  {t('account_settings:all_set')}!
                </Text>
              </HStack>

              <Text size="md" fontWeight={600} color="gray.700" lineHeight="sm">
                {t('account_settings:fields_updated')}
              </Text>
            </HStack>
            <Button variant="unstyled" minWidth={100} onPress={props.onClose}>
              {t('account_settings:continue')}
            </Button>
          </VStack>
        ) : (
          <Box alignItems="flex-start">
            <Button variant="unstyled" minWidth={100} onPress={props.onClose}>
              {t('account_settings:skip')}
            </Button>
          </Box>
        )}
      </VStack>

      {!!user?.userId && (
        <>
          <Modal noPadding isOpen={modalName === 'change-pin'} onClose={modalFocus.setHide} size="lg" _content={{ rounded: 'xl', w: 500 }}>
            <FormPinSetup userId={user.userId} onCancel={modalFocus.setHide} onEndChange={handleFormChange('update-pin')} />
          </Modal>

          <ModalConfirm
            isOpen={modalName === 'confirm-modal'}
            onClose={() => {
              setSelectedDefaultSiteId(user?.defaultSiteId);
              modalFocus.setHide();
            }}
            onConfirm={handleConfirmChanges}
          />
        </>
      )}
    </View>
  );
};
